import { useMemo, useRef, type ReactNode } from 'react';
import { createApiClient } from '@/shared/api';
import { getToken } from '@/shared/auth';
import { readEnv } from '@/shared/config';
import {
  createSchedulesApi,
  ScheduleCard,
  SchedulesApiProvider,
  type SchedulesApiClient,
} from '@/entities/schedule';
import { Button } from '@/shared/ui/button';
import { useSchedulesQuery } from '@/features/schedules';

const authProvider = { getToken };

function StateMessage({
  label,
  children,
}: {
  label: string;
  children: ReactNode;
}) {
  return (
    <section className="rounded-lg border border-border bg-card p-4" aria-label={label}>
      {children}
    </section>
  );
}

function SchedulesListContent() {
  const query = useSchedulesQuery();
  const schedules = query.data?.schedules ?? [];
  const summary = useMemo(() => {
    const total = query.data?.totalCount ?? 0;
    return `${total} ${total === 1 ? 'schedule' : 'schedules'}`;
  }, [query.data?.totalCount]);

  if (query.isLoading) {
    return (
      <StateMessage label="Schedules loading state">
        <p>Loading schedules...</p>
      </StateMessage>
    );
  }

  if (query.isError) {
    return (
      <StateMessage label="Schedules error state">
        <p>Unable to load schedules.</p>
        <Button
          className="mt-2 w-fit"
          variant="secondary"
          size="sm"
          type="button"
          onClick={() => {
            void query.refetch();
          }}
        >
          Retry
        </Button>
      </StateMessage>
    );
  }

  if (schedules.length === 0) {
    return (
      <StateMessage label="Schedules empty state">
        <p>No schedules yet.</p>
      </StateMessage>
    );
  }

  return (
    <section className="flex flex-col gap-4" aria-label="Schedules list">
      <p className="m-0 text-sm text-muted-foreground">{summary}</p>
      <ul className="m-0 grid list-none gap-3 p-0">
        {schedules.map((schedule) => (
          <li key={schedule.id}>
            <ScheduleCard schedule={schedule} />
          </li>
        ))}
      </ul>
    </section>
  );
}

export type SchedulesListWidgetProps = {
  client?: SchedulesApiClient;
};

export function SchedulesListWidget({ client }: SchedulesListWidgetProps) {
  const clientRef = useRef<SchedulesApiClient | null>(null);
  if (client === undefined && clientRef.current === null) {
    const baseUrl = readEnv('VITE_API_BASE_URL') ?? '';
    clientRef.current = createSchedulesApi(createApiClient({ baseUrl, auth: authProvider }));
  }
  const resolvedClient = client ?? clientRef.current;
  if (resolvedClient === null) {
    throw new Error('SchedulesListWidget could not initialize a schedules API client');
  }

  return (
    <SchedulesApiProvider client={resolvedClient}>
      <SchedulesListContent />
    </SchedulesApiProvider>
  );
}
